import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Tooltip, Typography } from '@material-tailwind/react'
import { colors } from '@material-tailwind/react/types/generic'
import { AiOutlineDown, AiOutlinePlus } from 'react-icons/ai'
import { useTranslation } from 'react-i18next'

import { useDarkMode } from '../contexts/DarkModeContext'
import {
  getContentLanguageDisplayName,
  getContentLanguageEndonym,
  normalizeLanguageSearch,
} from '../utils/constants/language.constants'

interface ContentLanguagePickerProps {
  languageCodes: readonly string[]
  onSelect: (code: string) => void
  disabled?: boolean
  buttonLabel: string
  buttonTestId: string
  optionTestIdPrefix: string
  buttonAriaLabel?: string
  buttonAriaDescribedBy?: string
  disabledTooltip?: string
  showAddIcon?: boolean
  containerClassName?: string
  buttonClassName?: string
}

/** Searchable content-language action built from the dashboard's existing Menu and Input primitives. */
const ContentLanguagePicker: React.FC<ContentLanguagePickerProps> = ({
  languageCodes,
  onSelect,
  disabled,
  buttonLabel,
  buttonTestId,
  optionTestIdPrefix,
  buttonAriaLabel,
  buttonAriaDescribedBy,
  disabledTooltip,
  showAddIcon = true,
  containerClassName = '',
  buttonClassName = '',
}) => {
  const { t, i18n } = useTranslation()
  const { darkMode } = useDarkMode()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<HTMLDivElement>(null)
  const uiLocale = i18n.resolvedLanguage ?? i18n.language ?? 'en'

  const options = useMemo(
    () =>
      languageCodes.map((code) => {
        const endonym = getContentLanguageEndonym(code)
        const displayName = getContentLanguageDisplayName(code, uiLocale)
        return {
          code,
          endonym,
          displayName,
          searchText: normalizeLanguageSearch(`${code} ${endonym} ${displayName}`),
        }
      }),
    [languageCodes, uiLocale]
  )

  const normalizedQuery = normalizeLanguageSearch(query)
  const filteredOptions = normalizedQuery
    ? options.filter((option) => option.searchText.includes(normalizedQuery))
    : options

  useEffect(() => {
    optionsRef.current?.scrollTo({ top: 0 })
  }, [normalizedQuery])

  useEffect(() => {
    if (!open) return

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => document.removeEventListener('mousedown', closeOnOutsideClick)
  }, [open])

  const trigger = (
    <Button
      size="sm"
      variant="outlined"
      color={(darkMode ? 'accent' : 'primary') as colors}
      className={`flex items-center gap-1 ${showAddIcon ? '' : 'justify-between'} ${buttonClassName}`}
      disabled={disabled}
      data-testid={buttonTestId}
      aria-label={buttonAriaLabel}
      aria-describedby={buttonAriaDescribedBy}
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => {
        setOpen((wasOpen) => !wasOpen)
        if (open) setQuery('')
      }}
    >
      {showAddIcon && <AiOutlinePlus />}
      <span>{buttonLabel}</span>
      {!showAddIcon && <AiOutlineDown aria-hidden="true" />}
    </Button>
  )

  return (
    <div ref={containerRef} className={`relative inline-block ${containerClassName}`}>
      {disabled && disabledTooltip ? (
        <Tooltip content={<span data-testid={`${buttonTestId}-disabled-tooltip`}>{disabledTooltip}</span>}>
          {/* Disabled buttons do not emit pointer/focus events: this wrapper is
              the tooltip trigger and remains keyboard reachable. */}
          <span
            className="inline-block"
            role="button"
            aria-disabled="true"
            tabIndex={0}
            data-testid={`${buttonTestId}-disabled-wrapper`}
          >
            {trigger}
          </span>
        </Tooltip>
      ) : (
        trigger
      )}

      {open && (
        <div
          className="absolute left-0 z-[9999] mt-2 w-80 rounded-md border border-blue-gray-50 bg-white p-2 shadow-lg dark:border-primary-300 dark:bg-primary"
          role="listbox"
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setOpen(false)
              setQuery('')
            }
          }}
        >
          <div className="pb-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.currentTarget.value)}
              label={t('localizations.searchLanguage')}
              data-testid="content-language-search"
              {...(darkMode && { color: 'blue-gray' })}
            />
          </div>

          <div ref={optionsRef} className="max-h-64 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map(({ code, endonym, displayName }) => (
                <button
                  type="button"
                  key={code}
                  onClick={() => {
                    onSelect(code)
                    setQuery('')
                    setOpen(false)
                  }}
                  className="block w-full rounded-md px-3 py-2 text-start transition-colors hover:bg-blue-gray-50 focus:bg-blue-gray-50 focus:outline-none dark:hover:bg-primary-300 dark:focus:bg-primary-300"
                  data-testid={`${optionTestIdPrefix}-${code}`}
                  role="option"
                  aria-selected={false}
                >
                  <span className="block text-primary dark:text-white">{endonym}</span>
                  <Typography variant="small" className="text-gray-600 dark:text-base-gray-500">
                    {displayName !== endonym ? `${displayName} · ` : ''}
                    {code}
                  </Typography>
                </button>
              ))
            ) : (
              <Typography
                variant="small"
                className="px-3 py-2 text-gray-600 dark:text-base-gray-500"
                data-testid="content-language-no-matches"
              >
                {t('localizations.noLanguageMatches')}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ContentLanguagePicker
